import { memo, useContext } from "react";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type IBaseEntity } from "types/apiResponse/apiResponse";
import { type DiscussionsLinkedTypes } from "types/enum";

import { formattedPlural } from "utilities/string/string";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconMessageFilled } from "commonComponents/btWrappers/BTIcon";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import {
    getLinkedEntityDetailPageLink,
    type IDiscussionLinkDetails,
} from "commonComponents/entity/comment/CommentComponent.utils";

export interface IDiscussionLinkProps extends IBaseEntity {
    entityType: DiscussionsLinkedTypes;
    commentCount: number;
    unreadCommentCount: number;
    buttonSize?: "small" | undefined;
    routeOverride?: string;
}

function DiscussionLinkInternal({
    id,
    entityType,
    builderId,
    jobId,
    commentCount,
    unreadCommentCount,
    buttonSize,
    routeOverride,
}: IDiscussionLinkProps) {
    const builderInfo = useContext(BuilderInfoContext);
    const unreadCommentDisplay =
        unreadCommentCount && unreadCommentCount > 0 ? ` (${unreadCommentCount} new)` : "";
    const discussionLinkDetails: IDiscussionLinkDetails = {
        linkedType: entityType,
        linkedId: id,
        jobId: jobId ?? 0,
        builderId,
        builderInfo,
    };
    let to = getLinkedEntityDetailPageLink(discussionLinkDetails);

    if (routeOverride) {
        to = {
            pathname: routeOverride,
            isRelativeLink: true,
            state: undefined,
        };
    }

    const linkContent = (
        <BTButtonDeprecated
            data-testid={`discussionLink_${id}`}
            type="link"
            isolated={buttonSize === "small" ? true : undefined}
        >
            <BTIconMessageFilled />
            <span>
                {formattedPlural({
                    value: commentCount,
                    zero: "Add Comment",
                    one: "1 Comment" + unreadCommentDisplay,
                    other: `${commentCount} Comments${unreadCommentDisplay}`,
                })}
            </span>
        </BTButtonDeprecated>
    );

    if (!to.pathname) {
        return linkContent;
    }
    if (to.isRelativeLink) {
        return <BTLinkRelative to={to.pathname}>{linkContent}</BTLinkRelative>;
    } else {
        return <BTLink to={to.pathname}>{linkContent}</BTLink>;
    }
}

export const DiscussionLink = memo(DiscussionLinkInternal);
